import React, { createContext, useContext, useCallback, useState } from 'react';
import { Alert, Snackbar, AlertColor } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

interface Notification {
  id: string;
  message: string;
  type: AlertColor;
  duration?: number;
}

interface NotificationContextType {
  showNotification: (message: string, type?: AlertColor, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  clearNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((
    message: string,
    type: AlertColor = 'info',
    duration = 5000
  ) => {
    const id = uuidv4();
    setNotifications(prev => [...prev, { id, message, type, duration }]);
  }, []);

  const showError = useCallback((message: string, duration = 5000) => {
    showNotification(message, 'error', duration);
  }, [showNotification]);

  const showSuccess = useCallback((message: string, duration = 3000) => {
    showNotification(message, 'success', duration);
  }, [showNotification]);

  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{
      showNotification,
      showError,
      showSuccess,
      clearNotification,
    }}>
      {children}
      {notifications.map(notification => (
        <Snackbar
          key={notification.id}
          open
          autoHideDuration={notification.duration}
          onClose={() => clearNotification(notification.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => clearNotification(notification.id)}
            severity={notification.type}
            variant="filled"
            elevation={6}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
} 